import { ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { OverlayModule } from '@angular/cdk/overlay';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { UserApiService } from 'src/app/api/services/user-api.service';
import { User } from 'src/app/api/services/user';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { ComponentsModule } from 'src/app/components/components.module';
import {
  ScrollingListFactoryService,
  ScrollingListInstance,
} from 'src/app/components/scrolling-list/services/scrolling-list-factory.service';

export type ShareAccessLevel =
  | 'Editor'
  | 'Publisher'
  | 'Playlist Manager'
  | 'Screen Sharing Moderator'
  | 'Full Access';
export type AccessControlList = Record<string, ShareAccessLevel | ''>;

export interface ShareWithUsersModalData {
  accessControlList?: AccessControlList | Promise<AccessControlList>;
  accessLevels: readonly ShareAccessLevel[];
  defaultAccessLevel: ShareAccessLevel;
  saveAccessControlList?: (
    accessControlList: AccessControlList,
    comment?: string,
  ) => Promise<void>;
}

interface UserAccess {
  id: string;
  accessLevel: ShareAccessLevel | '';
  user?: User;
}

@Component({
  selector: 'share-with-users-modal',
  templateUrl: './share-with-users-modal.component.html',
  standalone: true,
  host: {
    class: 'tw-block',
  },
  imports: [
    ComponentsModule,
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    OverlayModule,
  ],
})
export class ShareWithUsersModalComponent implements OnDestroy {
  filterConfig = {
    placeholder: 'Add emails (separated by commas)',
  };

  search: {
    companyId: string;
    sortBy: string;
    count: number;
    reverse: boolean;
    name: string;
    query?: string;
    onListUpdate: (newUsers?: User[]) => void;
  } = {
    companyId: this.companyStateService.getSelectedCompanyId(),
    sortBy: 'username',
    count: 10,
    reverse: false,
    name: 'Users',
    onListUpdate: (newUsers = []) => {
      newUsers.forEach((user) => this.cacheUser(user));
      this.searchPending = false;
      this.changeDetectorRef.detectChanges();
    },
  };
  users: ScrollingListInstance<User> = this.scrollingListFactory.create<User>(
    this.userApiService.list.bind(this.userApiService),
    this.search,
  );
  readonly accessLevels: readonly ShareAccessLevel[];
  initialAccessControlList: AccessControlList = {};
  userAccess: UserAccess[] = [];
  loadingAccessControlList = false;
  accessControlListError = false;
  newlyAddedUserIds = new Set<string>();
  newUserAccessLevel: ShareAccessLevel;
  sendEmailNotification = true;
  comment = '';
  saving = false;
  saveError = false;
  searchPending = false;
  searchFocused = false;
  private searchTimeout: ReturnType<typeof setTimeout> | undefined;

  constructor(
    public dialogRef: MatDialogRef<ShareWithUsersModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ShareWithUsersModalData,
    private companyStateService: CompanyStateService,
    private scrollingListFactory: ScrollingListFactoryService,
    private userApiService: UserApiService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.accessLevels = data.accessLevels;
    this.newUserAccessLevel = data.defaultAccessLevel;
    const accessControlList = data.accessControlList ?? {};
    if (
      typeof (accessControlList as Promise<AccessControlList>).then ===
      'function'
    ) {
      this.loadingAccessControlList = true;
      void this.loadAccessControlList(
        accessControlList as Promise<AccessControlList>,
      );
    } else {
      this.initializeAccessControlList(accessControlList as AccessControlList);
    }
  }

  private async loadAccessControlList(
    accessControlList: Promise<AccessControlList>,
  ): Promise<void> {
    try {
      this.initializeAccessControlList(await accessControlList);
    } catch {
      this.accessControlListError = true;
    } finally {
      this.loadingAccessControlList = false;
      this.changeDetectorRef.detectChanges();
    }
  }

  private initializeAccessControlList(
    accessControlList: AccessControlList,
  ): void {
    this.initialAccessControlList = { ...accessControlList };
    this.userAccess = Object.entries(this.initialAccessControlList)
      .filter(([, accessLevel]) => this.isSupportedAccessLevel(accessLevel))
      .map(([id, accessLevel]) => ({
        id,
        accessLevel: accessLevel as ShareAccessLevel,
      }));

    this.userAccess.forEach((entry) => void this.loadUser(entry));
  }

  get availableUsers(): User[] {
    return this.users.items.list.filter(
      (user) =>
        !this.userAccess.some((entry) => entry.id === this.userId(user)),
    );
  }

  get showUserSuggestions(): boolean {
    return (
      this.searchFocused && !this.searchPending && !this.users.loadingItems
    );
  }

  get newlyAddedUserAccess(): UserAccess[] {
    return this.userAccess.filter((entry) =>
      this.newlyAddedUserIds.has(entry.id),
    );
  }

  get existingUserAccess(): UserAccess[] {
    return this.userAccess.filter(
      (entry) => !this.newlyAddedUserIds.has(entry.id),
    );
  }

  get showNotificationFields(): boolean {
    return this.newlyAddedUserAccess.length > 0;
  }

  get showCommentField(): boolean {
    return this.showNotificationFields && this.sendEmailNotification;
  }

  get hasChanges(): boolean {
    const initialEntries = Object.entries(this.initialAccessControlList).filter(
      ([, accessLevel]) => this.isSupportedAccessLevel(accessLevel),
    );

    return (
      initialEntries.length !== this.userAccess.length ||
      this.userAccess.some(
        ({ id, accessLevel }) =>
          this.initialAccessControlList[id] !== accessLevel,
      )
    );
  }

  addUser(user: User): void {
    const id = this.userId(user);
    if (!id || this.userAccess.some((entry) => entry.id === id)) {
      return;
    }

    this.userAccess.push({
      id,
      accessLevel: this.newUserAccessLevel,
      user,
    });
    this.newlyAddedUserIds.add(id);
    this.search.query = '';
    this.searchPending = false;
    this.searchFocused = false;
    this.users.items.clear();
  }

  updateNewUserAccessLevel(accessLevel: ShareAccessLevel): void {
    this.newUserAccessLevel = accessLevel;
    this.userAccess.forEach((entry) => {
      if (this.newlyAddedUserIds.has(entry.id)) {
        entry.accessLevel = accessLevel;
      }
    });
  }

  searchUsers(): void {
    clearTimeout(this.searchTimeout);
    if (!this.search.query?.trim()) {
      this.searchPending = false;
      this.users.items.clear();
      return;
    }

    this.searchPending = true;
    this.searchTimeout = setTimeout(() => this.users.doSearch(), 300);
  }

  focusSearch(): void {
    this.searchFocused = true;
  }

  blurSearch(): void {
    this.searchFocused = false;
  }

  closeSearchOnEscape(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.blurSearch();
    }
  }

  removeUser(userId: string): void {
    this.userAccess = this.userAccess.filter((entry) => entry.id !== userId);
    this.newlyAddedUserIds.delete(userId);
  }

  updateAccessLevel(
    entry: UserAccess,
    accessLevel: ShareAccessLevel | '',
  ): void {
    entry.accessLevel = accessLevel;
  }

  userName(entry: UserAccess): string {
    const fullName =
      `${entry.user?.firstName ?? ''} ${entry.user?.lastName ?? ''}`.trim();
    return fullName || entry.id;
  }

  userEmail(entry: UserAccess): string {
    return entry.user?.email || entry.id;
  }

  accessSelectWidth(accessLevel: ShareAccessLevel | ''): string {
    const label = accessLevel || 'Remove Access';
    const width = Math.min(240, Math.max(120, label.length * 8 + 64));

    return `${width}px`;
  }

  dismiss(): void {
    this.dialogRef.close(false);
  }

  ngOnDestroy(): void {
    clearTimeout(this.searchTimeout);
  }

  async apply(): Promise<void> {
    const accessControlList: AccessControlList = {};
    Object.keys(this.initialAccessControlList).forEach((id) => {
      accessControlList[id] = '';
    });
    this.userAccess.forEach(({ id, accessLevel }) => {
      accessControlList[id] = this.newlyAddedUserIds.has(id)
        ? this.newUserAccessLevel
        : accessLevel;
    });

    if (!this.data.saveAccessControlList) {
      this.dialogRef.close(accessControlList);
      return;
    }

    this.saving = true;
    this.saveError = false;
    this.dialogRef.disableClose = true;

    try {
      await this.data.saveAccessControlList(
        accessControlList,
        this.showCommentField ? this.comment : undefined,
      );
      this.dialogRef.close(accessControlList);
    } catch {
      this.saveError = true;
    } finally {
      this.saving = false;
      this.dialogRef.disableClose = false;
    }
  }

  private async loadUser(entry: UserAccess): Promise<void> {
    try {
      entry.user = await this.userApiService.getProfile(entry.id);
      this.changeDetectorRef.detectChanges();
    } catch {
      // Keep the ACL user visible by ID when profile lookup is unavailable.
    }
  }

  private cacheUser(user: User): void {
    const id = this.userId(user);
    const entry = this.userAccess.find((item) => item.id === id);
    if (entry) {
      entry.user = user;
    }
  }

  private userId(user: User): string {
    return user.username || user.id || user.email;
  }

  private isSupportedAccessLevel(
    accessLevel: string,
  ): accessLevel is ShareAccessLevel {
    return this.accessLevels.includes(accessLevel as ShareAccessLevel);
  }
}
